import { ICryptoRequest } from 'src/app/shared/domain/crypto-request.entity';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { RequestMakerHelpers } from 'src/app/shared/helpers/request-maker.helper';
import { map } from 'rxjs/operators';
import { IntegrationFlow } from 'src/app/shared/domain/enums/integration-flow.enum';
import { DMSConnectState } from '../domain/dms-connect.entity';

@Injectable()
export class IntegrationService {
  apiUrl = environment.apiBaseUrl + 'integration/';

  constructor(
    private httpClient: HttpClient,
    private requestMakerHelper: RequestMakerHelpers) {
  }

  query(body: IIntegrationSearchRequest): Observable<IIntegration[]> {
    return this.httpClient.post<IIntegration[]>(this.apiUrl, body, this.getOptions())
    .pipe(
      map(response => {
        const ret = [] as IIntegration[];
        response.forEach(item => {
          if (item.flowId === IntegrationFlow.SEND_EMAIL_CONFIRMATION && !item.integrationEmail) {
            item.isConfigured = false;
          }
          if (item.isConfigured) {
            item.formStatusText = item.formStatus === 1 ? 'Active' : 'In process';
            ret.push(item);
          }
        });
        return ret;
      })
    );
  }

  getIntegrationTypeLk(): Observable<IIntegrationTypeLk[]> {
    return this.httpClient.get<IIntegrationTypeLk[]>(this.apiUrl + 'types', this.getOptions());
  }

  getIntegrationTypeLkById(integrationTypeId: number): Observable<IIntegrationTypeLk> {
    const body = this.requestMakerHelper.getIRequestIdEncrypted(integrationTypeId);
    return this.httpClient.post<IIntegrationTypeLk>(this.apiUrl + 'types/id', body, this.getOptions());
  }

  getConfigByProvider(providerId: number): Observable<IIntegrationConfigField[]> {
    const body = this.requestMakerHelper.getIRequestIdEncrypted(providerId);
    return this.httpClient.post<IIntegrationConfigField[]>(this.apiUrl + 'config', body, this.getOptions());
  }

  getIntegrationSettingsValues(shopIntegrationId: number): Observable<IntegrationSettingsPendingApproval> {
    const body = this.requestMakerHelper.getIRequestIdEncrypted(shopIntegrationId);
    return this.httpClient.post<IntegrationSettingsPendingApproval>(this.apiUrl + 'values', body, this.getOptions())
    .pipe(
      map(response => {
        response.details.forEach(detail => {
          // TODO: REMOVE THIS
          if (detail.permission) {
            detail.isReadOnly = detail.permission.toUpperCase() === 'R';
          }
        });
        return response;
      })
    );
  }

  submit(body: IIntegrationConfigSaveRequest): Observable<void> {
    return this.httpClient.post<void>(this.apiUrl + 'submit', body, this.getOptions());
  }

  update(body: IUpdateRequest): Observable<void> {
    return this.httpClient.post<void>(this.apiUrl + 'status/update', body, this.getOptions());
  }

  confirm(body: IIntegrationConfigSaveRequest): Observable<void> {
    return this.httpClient.post<void>(this.apiUrl + 'form/validation/save', body, this.getOptions());
  }

  sync(): Observable<void> {
    return this.httpClient.post<void>(this.apiUrl + 'sync', this.getOptions());
  }

  hash(hash: string): Observable<IntegrationSettingsPendingApproval> {
    const endpoint = `form/validation/${hash}`;
    return this.httpClient.get<IntegrationSettingsPendingApproval>(this.apiUrl + endpoint, this.getOptions());
  }

  getDmsConnectSetting(): Observable<DMSConnectState> {
    const endpoint = `dmsconnect/settings`;
    return this.httpClient.get<DMSConnectState>(this.apiUrl + endpoint, this.getOptions());
  }

  private getOptions() {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });
    return { headers };
  }
}

export interface IIntegrationSearchRequest extends ICryptoRequest {
  pageNum: number;
  pageSize: number;
  orderBy: string;
}

export interface IIntegration {
  shopIntegrationId: number;
  createDate: string;
  integrationTypeId: number;
  typeDescription: string;
  integrationCategoryId: number;
  categoryDescription: string;
  status: string;
  formStatus: number;
  formStatusText: string;
  flowId: number;
  integrationEmail: string;

  accountNum?: string;
  isConfigured?: boolean;
}

export interface IIntegrationTypeLk {
  integrationTypeId: number;
  description: string;
  integrationCategoryId?: number;
  supplierGroupLkId?: number;
}

export interface IIntegrationConfigField {
  fieldValueId: number;
  shopIntegrationConfigFieldId: number;
  fieldText: string;
  type: string;
  isNullable: number;
  permission: string;
  entityType: string;

  // UI Properties
  value: any;
  isReadOnly: boolean;
}

export interface IIntegrationFieldRequest {
  shopIntegartionConfigFieldId: number;
  value: any;
}

export interface IIntegrationConfigSaveRequest extends ICryptoRequest {
  header: IIntegrationConfigSaveHeader;
  details: IIntegrationConfigSaveField[];
}

export interface IIntegrationConfigSaveHeader {
  integrationTypeId?: number;
  contactMakeId?: number;
  providerEmail?: string;
  flow: number;
  formShopIntegrationId: number;
  formHash?: string;
}

export interface IIntegrationConfigSaveField {
  fieldId: number;
  fieldValue: string;
}

export interface IIntegrationFieldConfirmRequest extends ICryptoRequest {
  shopIntegartionConfigFieldId: number;
  isValid: boolean;
}

export interface IntegrationSettingsPendingApprovalHeader {
  provider: string;
  userName: string;
  companyName: string;
  integrationEmail: string;
  formStatus: number;
  flowId: number;
  shopAddress: string;
  shopCity: string;
  shopState: string;
  formShopIntegrationId: number;
}

export interface IntegrationSettingsPendingApproval {
  header: IntegrationSettingsPendingApprovalHeader;
  details: IIntegrationConfigField[];
}

export interface IUpdateRequest extends ICryptoRequest {
  id: number;
  enabled: boolean;
}
